import { createContext } from 'react'
import type {
    SignInCredential,
    SignUpCredential,
    AuthResult,
    TwoFactorResult,
    User,
    OauthSignInCallbackPayload,
} from '@/@types/auth'

type Auth = {
    authenticated: boolean
    user: User
    signIn: (values: SignInCredential) => AuthResult
    signUp: (values: SignUpCredential) => AuthResult
    signOut: () => void
    /**
     * Sign in with Google through Firebase.
     *
     * Takes no argument, unlike the Google Identity Services version it
     * replaced: the popup is opened by this call rather than by a Google-owned
     * button that handed back a credential.
     */
    signInWithGoogle: () => AuthResult
    signInWithMicrosoft: () => AuthResult
    /** Always reports success — the backend never says whether the address exists. */
    sendPasswordReset: (email: string) => AuthResult
    resendVerificationEmail: (email: string) => AuthResult
    /**
     * Finish a sign-in that asked for a second factor: the challenge from the
     * 403 plus a code from the authenticator app or a recovery code.
     */
    verifyTwoFactor: (challenge: string, code: string) => TwoFactorResult
    oAuthSignIn: (
        callback: (payload: OauthSignInCallbackPayload) => void,
    ) => void
}

const defaultFunctionPlaceHolder = async (): AuthResult => {
    await new Promise((resolve) => setTimeout(resolve, 0))
    return {
        status: '',
        message: '',
    }
}

const defaultOAuthSignInPlaceHolder = (
    callback: (payload: OauthSignInCallbackPayload) => void,
): void => {
    callback({
        onSignIn: () => {},
        redirect: () => {},
    })
}

const AuthContext = createContext<Auth>({
    authenticated: false,
    user: {},
    signIn: async () => defaultFunctionPlaceHolder(),
    signUp: async () => defaultFunctionPlaceHolder(),
    signOut: () => {},
    signInWithGoogle: async () => defaultFunctionPlaceHolder(),
    signInWithMicrosoft: async () => defaultFunctionPlaceHolder(),
    sendPasswordReset: async () => defaultFunctionPlaceHolder(),
    resendVerificationEmail: async () => defaultFunctionPlaceHolder(),
    verifyTwoFactor: async () => defaultFunctionPlaceHolder(),
    oAuthSignIn: defaultOAuthSignInPlaceHolder,
})

export default AuthContext
